import { useCallback, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";

import type { TermDetail } from "@/api/db/terms";
import { composePages } from "@/api/document/block";
import type { DocumentBlock, DocumentPage } from "@/api/document/block";
import { FloatingPlacement } from "@/api/document/floats";
import type { FloatDraft, FloatImage } from "@/api/document/floats";
import { A4_METRICS } from "@/api/document/paginate";
import type { FloatSelection } from "@/components/document-editor/floating-layer";
import type { ImageActions, ImageFloatRequest } from "@/components/document-editor/image-actions";
import { PAGE_STRIDE } from "@/components/document-editor/pagination";

function clamp(value: number, min: number, max: number) {
	return Math.min(max, Math.max(min, value));
}

function itemAtOrNull(items: string[], index: number) {
	const item = items.at(index);

	if (item === undefined) {
		return null;
	}

	return item;
}

function floatDraft(float: FloatImage): FloatDraft {
	return {
		file: float.file,
		anchorId: float.anchorId,
		pageId: float.pageId,
		x: float.x,
		y: float.y,
		width: float.width,
		height: float.height,
	};
}

function snapshot(pageIds: string[], groups: string[][], floats: FloatImage[]) {
	return JSON.stringify({ pageIds, groups, floats: floats.map(floatDraft) });
}

export function useTermFloats(input: {
	term: TermDetail;
	initialPages: DocumentPage[];
	editor: Editor;
}) {
	const { term, editor } = input;
	const initialGroups = input.initialPages.map((page) =>
		page.content.map((block) => block.attrs.id),
	);
	const initialPageIds = input.initialPages.map((page) => page.attrs.id);
	const [floats, setFloats] = useState<FloatImage[]>(term.floats);
	const [selection, setSelection] = useState<FloatSelection | null>(null);
	const [groups, setGroups] = useState(initialGroups);
	const [pageIds, setPageIds] = useState(initialPageIds);
	const savedSnapshot = useRef(snapshot(initialPageIds, initialGroups, term.floats));
	const nextLocalId = useRef(-1);
	const floatsRef = useRef(floats);
	floatsRef.current = floats;
	const groupsRef = useRef(groups);
	groupsRef.current = groups;
	const pageIdsRef = useRef(pageIds);
	pageIdsRef.current = pageIds;
	const blockOrder = useRef(term.blocks.map((block) => block.attrs.id));

	const patchFloat = (id: number, patch: Partial<FloatImage>) => {
		setFloats((current) =>
			current.map((float) => (float.id === id ? { ...float, ...patch } : float)),
		);
	};

	const cloneFurniture = (sourcePageId: string | undefined, targetPageIds: string[]) => {
		if (sourcePageId === undefined) {
			return;
		}

		const furniture = floatsRef.current.filter((float) => float.pageId === sourcePageId);
		const created = targetPageIds.flatMap((pageId) =>
			furniture.map((source) => {
				const id = nextLocalId.current;
				nextLocalId.current -= 1;

				return { ...source, id, anchorId: null, pageId };
			}),
		);

		if (created.length > 0) {
			setFloats((current) => [...current, ...created]);
		}
	};

	const syncPagesImpl = (visual: string[][]) => {
		const nextGroups = visual.length === 0 ? [[]] : visual;
		groupsRef.current = nextGroups;
		setGroups(nextGroups);

		if (nextGroups.length === pageIdsRef.current.length) {
			return pageIdsRef.current;
		}

		if (nextGroups.length > pageIdsRef.current.length) {
			const added = Array.from({ length: nextGroups.length - pageIdsRef.current.length }, () =>
				crypto.randomUUID(),
			);
			const sourcePageId = pageIdsRef.current.at(-1);
			const next = [...pageIdsRef.current, ...added];

			pageIdsRef.current = next;
			setPageIds(next);
			cloneFurniture(sourcePageId, added);

			return next;
		}

		const next = pageIdsRef.current.slice(0, nextGroups.length);
		const removed = new Set(pageIdsRef.current.slice(nextGroups.length));

		pageIdsRef.current = next;
		setPageIds(next);
		setFloats((current) =>
			current.filter((float) => float.pageId === null || !removed.has(float.pageId)),
		);

		return next;
	};

	const syncPagesRef = useRef(syncPagesImpl);
	syncPagesRef.current = syncPagesImpl;

	const syncPages = useCallback((visual: string[][]) => syncPagesRef.current(visual), []);

	const pagesForSave = (blocks: DocumentBlock[]) =>
		composePages(blocks, groupsRef.current, syncPagesRef.current(groupsRef.current));

	const reanchorImpl = (blocks: DocumentBlock[]) => {
		const order = blocks.map((block) => block.attrs.id);
		const previous = blockOrder.current;
		blockOrder.current = order;
		const surviving = new Set(order);

		setFloats((current) =>
			current.map((float) => {
				if (float.anchorId === null || surviving.has(float.anchorId)) {
					return float;
				}

				return {
					...float,
					anchorId: FloatingPlacement.reanchor(previous, order, float.anchorId),
				};
			}),
		);
	};

	const reanchorRef = useRef(reanchorImpl);
	reanchorRef.current = reanchorImpl;

	const reanchor = useCallback((blocks: DocumentBlock[]) => {
		reanchorRef.current(blocks);
	}, []);

	const flowToFloat = (request: ImageFloatRequest) => {
		const strip = request.dom.closest(".doc-a4-strip");
		const node = editor.state.doc.nodeAt(request.pos);

		if (strip === null || node === null) {
			return;
		}

		const stripRect = strip.getBoundingClientRect();
		const rect = request.dom.getBoundingClientRect();
		const offsetTop = rect.top - stripRect.top;
		const page = Math.max(0, Math.floor(offsetTop / PAGE_STRIDE));
		const x = Math.round(
			clamp(rect.left - stripRect.left, 0, A4_METRICS.pageWidth - request.width),
		);
		const y = Math.round(
			clamp(offsetTop - page * PAGE_STRIDE, 0, A4_METRICS.pageHeight - request.height),
		);
		const index = editor.state.doc.resolve(request.pos).index(0);
		const anchors: string[] = [];

		editor.state.doc.forEach((child, _, childIndex) => {
			if (childIndex !== index && typeof child.attrs.id === "string") {
				anchors.push(child.attrs.id);
			}
		});

		const id = nextLocalId.current;
		const anchorId = itemAtOrNull(anchors, clamp(index - 1, 0, anchors.length - 1));
		nextLocalId.current -= 1;

		editor
			.chain()
			.deleteRange({ from: request.pos, to: request.pos + node.nodeSize })
			.run();
		setFloats((current) => [
			...current,
			{
				id,
				file: request.file,
				anchorId,
				pageId: null,
				x,
				y,
				width: request.width,
				height: request.height,
			},
		]);
		setSelection({ id, page });
	};

	const flowToFloatRef = useRef(flowToFloat);
	flowToFloatRef.current = flowToFloat;

	const imageActions = useMemo<ImageActions>(
		() => ({ toFloat: (request) => flowToFloatRef.current(request) }),
		[],
	);

	const onRemove = (id: number) => {
		setFloats((current) => current.filter((float) => float.id !== id));
		setSelection(null);
	};

	const flowInsertPos = (float: FloatImage) => {
		let pos = editor.state.doc.content.size;

		if (float.pageId !== null) {
			const pageIndex = pageIdsRef.current.indexOf(float.pageId);
			const firstBlockId = groupsRef.current[pageIndex]?.at(0);

			editor.state.doc.forEach((child, childOffset) => {
				if (child.attrs.id === firstBlockId) {
					pos = childOffset;
				}
			});

			return pos;
		}

		editor.state.doc.forEach((child, childOffset) => {
			if (child.attrs.id === float.anchorId) {
				pos = childOffset + child.nodeSize;
			}
		});

		return pos;
	};

	const onToFlow = (id: number) => {
		const float = floats.find((entry) => entry.id === id);

		if (float === undefined) {
			return;
		}

		const { width, height } = FloatingPlacement.fitToFlow(term.margins, float);

		editor
			.chain()
			.focus()
			.insertContentAt(flowInsertPos(float), {
				type: "image",
				attrs: { file: float.file, width, height },
			})
			.run();

		onRemove(id);
	};

	const currentSnapshot = snapshot(pageIds, groups, floats);

	return {
		floats,
		selection,
		setSelection,
		pages: groups.map((blocks, index) => ({ id: pageIds[index], blocks })),
		imageActions,
		reanchor,
		syncPages,
		pagesForSave,
		floatsForSave: () => floatsRef.current.map(floatDraft),
		dirty: currentSnapshot !== savedSnapshot.current,
		markSaved: () => {
			savedSnapshot.current = snapshot(pageIdsRef.current, groupsRef.current, floatsRef.current);
		},
		onCommit: patchFloat,
		onToFlow,
		onRemove,
	};
}
